import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { memo } from "react";
import { Box } from "@mui/material";
import { cn } from "../../lib/utils";
import { attachmentUrl } from "../../lib/attachments";

// MarkdownText that uses assistant-ui context (for normal message rendering)
const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md"
      components={defaultComponents}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

// StandaloneMarkdown that accepts a text prop (for filtered/processed text)
const StandaloneMarkdownImpl = ({ text }) => {
  if (!text) return null;
  return (
    <div className="aui-md">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {text}
      </Markdown>
    </div>
  );
};

export const StandaloneMarkdown = memo(StandaloneMarkdownImpl);

// Shared styling for tables (MUI sx). Wide product catalogs would overflow the
// chat area without the wrapper, and without word-break long cell text stretches
// the layout sideways.
const tableWrapSx = {
  my: 2,
  width: '100%',
  overflowX: 'auto',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
};
const tableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
};
const thSx = {
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: 'action.hover',
  px: 1.5,
  py: 1,
  textAlign: 'left',
  fontWeight: 600,
  verticalAlign: 'top',
  wordBreak: 'break-word',
};
const tdSx = {
  borderBottom: 1,
  borderColor: 'divider',
  px: 1.5,
  py: 1,
  verticalAlign: 'top',
  wordBreak: 'break-word',
};

// react-markdown passes a `node` prop (the AST node). We strip it so it doesn't
// land on the DOM as an unknown `node="[object Object]"` attribute.
const stripNode = ({ node, ...rest }) => rest;

// Images in chat markdown. `![alt](attachment:file_xxx)` — the reference
// syntax the media architecture uses in exports — also works in CHAT: we
// rewrite it to the same /attachments endpoint history images already use.
// Empty/garbage srcs (weak models emit them) render as the alt text instead
// of a broken-image icon.
const resolveImgSrc = (src) => {
  if (!src) return null;
  if (src.startsWith("attachment:")) {
    const id = src.slice("attachment:".length).trim();
    return id ? attachmentUrl(id) : null;
  }
  if (src.startsWith("data:image/") || src.startsWith("http://") || src.startsWith("https://") || src.startsWith("/")) {
    return src;
  }
  return null; // unknown scheme → don't render a broken image
};

const ImgRenderer = ({ node, src, alt, ...props }) => {
  const resolved = resolveImgSrc(src);
  if (!resolved) {
    return alt ? <em style={{ fontSize: "0.85em", opacity: 0.7 }}>[{alt}]</em> : null;
  }
  return (
    <img
      src={resolved}
      alt={alt || ""}
      loading="lazy"
      style={{ maxWidth: "100%", borderRadius: 8, margin: "8px 0", display: "block" }}
      {...props}
    />
  );
};

// Links: `[name](attachment:file_xxx)` — same reference syntax as images —
// resolve to the /attachments endpoint so the model can hand the user a
// working open/download link for any materialized file.
const LinkRenderer = ({ node, href, children, ...props }) => {
  let resolved = href;
  if (href && href.startsWith("attachment:")) {
    const id = href.slice("attachment:".length).trim();
    resolved = id ? attachmentUrl(id) : undefined;
  }
  if (!resolved) {
    return <>{children}</>;
  }
  return (
    <a href={resolved} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
};

// Components for react-markdown (slightly different API than assistant-ui).
// Exported so other components rendering Markdown directly (e.g. history
// playback in thread.jsx) can apply the same table/code styling.
export const markdownComponents = {
  a: LinkRenderer,
  img: ImgRenderer,
  p: ({ node, className, ...props }) => (
    <p
      className={cn(
        "aui-md-p",
        className,
      )}
      style={{ marginTop: '1rem', marginBottom: '1rem', lineHeight: 1.7 }}
      {...props}
    />
  ),
  code: ({ node, className, inline, ...props }) => (
    <code
      className={cn(inline && "aui-md-inline-code", className)}
      style={inline ? {
        background: 'rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 4,
        padding: '0 4px',
        fontWeight: 600,
      } : undefined}
      {...props}
    />
  ),
  table: (props) => (
    <Box component="div" sx={tableWrapSx}>
      <Box component="table" sx={tableSx} {...stripNode(props)} />
    </Box>
  ),
  thead: (props) => <thead {...stripNode(props)} />,
  tbody: (props) => <tbody {...stripNode(props)} />,
  tr: (props) => <tr {...stripNode(props)} />,
  th: (props) => <Box component="th" sx={thSx} {...stripNode(props)} />,
  td: (props) => <Box component="td" sx={tdSx} {...stripNode(props)} />,
};

const defaultComponents = memoizeMarkdownComponents({
  a: LinkRenderer,
  p: ({ node, className, ...props }) => (
    <p
      className={cn("aui-md-p", className)}
      style={{ marginTop: '1rem', marginBottom: '1rem', lineHeight: 1.7 }}
      {...props}
    />
  ),
  code: function Code({ node, className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(!isCodeBlock && "aui-md-inline-code", className)}
        style={!isCodeBlock ? {
          background: 'rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: 4,
          padding: '0 4px',
          fontWeight: 600,
        } : undefined}
        {...props}
      />
    );
  },
  table: (props) => (
    <Box component="div" sx={tableWrapSx}>
      <Box component="table" sx={tableSx} {...stripNode(props)} />
    </Box>
  ),
  thead: (props) => <thead {...stripNode(props)} />,
  tbody: (props) => <tbody {...stripNode(props)} />,
  tr: (props) => <tr {...stripNode(props)} />,
  th: (props) => <Box component="th" sx={thSx} {...stripNode(props)} />,
  td: (props) => <Box component="td" sx={tdSx} {...stripNode(props)} />,
});
